import { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { VALID_DIMENSION_FILTERS, MATCHING_SETS } from '../sbSharedRefineMenuConstants';
import { ServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { SbRespRefineMenuDrawerAppliedFilterDisplay } from '../../SbRespRefineMenuDrawer/SbRespRefineMenuDrawerAppliedFilterDisplay';

// components
import { SbSharedRefineMenuFacet } from '../SbSharedRefineMenuFacet/SbSharedRefineMenuFacet';

function SbSharedRefineMenuFiltersComponent({
    user,
    viewer,
    filters,
    currency,
    isClient,
    filterMap,
    itemSearch,
    initiallyExpanded,
}) {
    let dimensionsDisplayed = false;
    const { isSbTopNavVariant } = useContext(ServerVarsContext);
    const intl = useIntl();

    return (
        <Fragment>
            {filters.map(filter => {
                const { values, totalCount, name: filterName, localizedFilterName: title } = filter;
                const { component: FilterComponent, title: FilterTitle } =
                    filterMap[filterName] || {};
                const titleOverride = FilterTitle && intl.formatMessage(FilterTitle);
                /**
                 * height, width, length and depth are returned as separate filters but need to be
                 * displayed in one "Dimensions" facet.  When the first valid dimension type filter is hit
                 * we will setup the dimensions facet.  Once the dimensions facet is setup we will
                 * ignore the rest of the dimension filters.
                 *
                 * The dimensions filter component will account and setup all available dimension filters in one facet
                 */

                if (VALID_DIMENSION_FILTERS.includes(filterName)) {
                    if (dimensionsDisplayed) {
                        return null;
                    }
                    dimensionsDisplayed = true;
                }

                if (filterName === MATCHING_SETS && !viewer.isMatchedSetsEnabled) {
                    return null;
                }

                return (
                    FilterComponent && (
                        <SbSharedRefineMenuFacet
                            key={filterName}
                            storageFacetKey={filterName}
                            title={titleOverride || title}
                            initiallyExpanded={initiallyExpanded(filterName)}
                            appliedFiltersComponent={
                                isSbTopNavVariant ? (
                                    <SbRespRefineMenuDrawerAppliedFilterDisplay
                                        itemSearch={itemSearch}
                                        user={user}
                                        filterName={filterName}
                                        currency={currency}
                                    />
                                ) : null
                            }
                        >
                            <FilterComponent
                                user={user}
                                viewer={viewer}
                                values={values}
                                filter={filter}
                                filters={filters}
                                currency={currency}
                                isClient={isClient}
                                totalCount={totalCount}
                                filterName={filterName}
                                itemSearch={itemSearch}
                                shouldUseExpandableList={!isSbTopNavVariant}
                            />
                        </SbSharedRefineMenuFacet>
                    )
                );
            })}
        </Fragment>
    );
}

SbSharedRefineMenuFiltersComponent.defaultProps = {
    initiallyExpanded: () => true,
};

SbSharedRefineMenuFiltersComponent.propTypes = {
    user: PropTypes.object,
    isClient: PropTypes.bool,
    filters: PropTypes.array.isRequired,
    viewer: PropTypes.object.isRequired,
    currency: PropTypes.string.isRequired,
    filterMap: PropTypes.object.isRequired,
    itemSearch: PropTypes.object.isRequired,
    initiallyExpanded: PropTypes.func.isRequired,
    uriRef: PropTypes.string,
    isTrade: PropTypes.bool,
};

export const SbSharedRefineMenuFilters = createFragmentContainer(
    SbSharedRefineMenuFiltersComponent,
    {
        viewer: graphql`
            fragment SbSharedRefineMenuFilters_viewer on Viewer {
                isMatchedSetsEnabled: isEnabled(key: "enableMatchedSetsFacet")
                ...SbSharedRefineMenuFilterMetal_viewer
                ...SbSharedRefineMenuFilterStone_viewer
                ...SbSharedRefineMenuFilterOrigin_viewer
                ...SbSharedRefineMenuFilterCreator_viewer
                ...SbSharedRefineMenuFilterMaterial_viewer
                ...SbRespRefineMenuFilterDeliveryOptions_viewer
                ...SbMobileRefineMenuFilterDeliveryOptions_viewer
                ...SbRespRefineMenuFilterStoneCut_viewer
                ...SbMobileRefineMenuFilterStoneCut_viewer
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefineMenuFilters_itemSearch on ItemSearchQueryConnection {
                ...SbRespRefineMenuDrawerAppliedFilterDisplay_itemSearch
                ...SbRespRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStone_itemSearch
                ...SbSharedRefineMenuFilterMetal_itemSearch
                ...SbSharedRefineMenuFilterColor_itemSearch
                ...SbMobileRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStyle_itemSearch
                ...SbSharedRefineMenuFilterOrigin_itemSearch
                ...SbSharedRefineMenuFilterPeriod_itemSearch
                ...SbRespRefineMenuFilterCategory_itemSearch
                ...SbRespRefineMenuFilterStoneCut_itemSearch
                ...SbSharedRefineMenuFilterCreator_itemSearch
                ...SbMobileRefineMenuFilterStoneCut_itemSearch
                ...SbMobileRefineMenuFilterCategory_itemSearch
                ...SbSharedRefineMenuFilterMaterial_itemSearch
                ...SbRespRefineMenuFilterDeliveryOptions_itemSearch
                ...SbMobileRefineMenuFilterDeliveryOptions_itemSearch
                ...SbSharedRefineMenuFilterItemType_itemSearch
                ...SbRespRefineMenuFilterDimensions_itemSearch
                ...SbRespRefineMenuFilterCaratWeight_itemSearch
                ...SbMobileRefineMenuFilterDimensions_itemSearch
                ...SbMobileRefineMenuFilterCaratWeight_itemSearch
                ...SbSharedRefineMenuFilterAvailability_itemSearch
                ...SbSharedRefineMenuFilterMatchingSets_itemSearch
                ...SbSharedRefineMenuFilterGender_itemSearch
                appliedFilters {
                    name
                    values {
                        displayName
                    }
                }
            }
        `,
        filters: graphql`
            fragment SbSharedRefineMenuFilters_filters on SearchBrowseFilter @relay(plural: true) {
                name
                totalCount
                localizedFilterName
                ...SbRespRefineMenuFilterCategory_filters
                ...SbMobileRefineMenuFilterCategory_filters
                ...SbRespRefineMenuFilterDimensions_filters
                ...SbMobileRefineMenuFilterDimensions_filters
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    stats {
                        key
                        values
                    }
                }
            }
        `,
        user: graphql`
            fragment SbSharedRefineMenuFilters_user on User {
                ...SbRespRefineMenuDrawerAppliedFilterDisplay_user
                ...SbRespRefineMenuFilterPrice_user
                ...SbRespRefineMenuFilterDimensions_user
                ...SbMobileRefineMenuFilterPrice_user
                ...SbMobileRefineMenuFilterDimensions_user
            }
        `,
    }
);
